import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  ArrowLeft, Loader2, Users, Shield, DollarSign, Wallet, Trash2, UserPlus,
  Mail, Phone, Building2, Briefcase, CheckCircle, XCircle, Copy, Share2, Landmark, AlertTriangle, PauseCircle, PlayCircle,
  Plus, Pencil, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

function BankingSection({ company, onAccountCreated }) {
  const [creating, setCreating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const hasAccount = !!company.virtual_account_number;

  const accountName = company.virtual_account_reference || company.name;
  const accountNumber = company.virtual_account_number;
  const bankName = company.virtual_account_bank || 'Asset Microfinance Bank';
  const bankCode = '090160';

  const handleCreate = async () => {
    setCreating(true);
    try {
      const result = await superAdminAPI.createVirtualAccount(company.id);
      toast.success(`Virtual account ${result.account_number} created`);
      setShowConfirm(false);
      onAccountCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create virtual account');
    } finally { setCreating(false); }
  };

  const copyNumber = () => {
    navigator.clipboard.writeText(accountNumber);
    toast.success('Account number copied');
  };

  const shareDetails = () => {
    const msg = `Your Pebbles funding account details are ready.\n\nAccount Name: ${accountName}\nAccount Number: ${accountNumber}\nBank: Assets MFB\nBank Code: ${bankCode}\n\nPlease send funds to this account to load your Pebbles wallet. Your dashboard balance will update automatically once funds are received.`;
    navigator.clipboard.writeText(msg);
    toast.success('Account details copied — ready to share');
  };

  if (!hasAccount) {
    return (
      <Card className="border-orange-200 bg-orange-50/30 md:col-span-2" data-testid="banking-no-account">
        <CardContent className="p-6 text-center space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center mx-auto">
            <Landmark className="w-7 h-7 text-orange-500" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">No Virtual Account</h3>
            <p className="text-xs text-gray-500 mt-1">This company has not been provisioned with a funding account on Assets MFB yet.</p>
          </div>
          <Button onClick={() => setShowConfirm(true)} data-testid="create-virtual-account-btn"
            className="bg-orange-500 hover:bg-orange-600 text-white">
            <Landmark className="w-4 h-4 mr-2" /> Create Virtual Account
          </Button>
          <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle className="text-base">Confirm Account Creation</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">This will create a real production account with Assets MFB under master account 1000120542. Confirm?</p>
                </div>
                <div className="text-xs text-gray-600 space-y-1">
                  <p><strong>Company:</strong> {company.name}</p>
                  <p><strong>Email:</strong> {company.email}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setShowConfirm(false)}>Cancel</Button>
                  <Button onClick={handleCreate} disabled={creating} data-testid="confirm-create-btn"
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm & Create'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-emerald-200 bg-emerald-50/30 md:col-span-2" data-testid="banking-active-account">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Landmark className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Funding Account</h3>
              <p className="text-[10px] text-emerald-600 font-medium">Active on Assets MFB</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={shareDetails} data-testid="share-details-btn"
            className="h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50">
            <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share Details
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Account Number</p>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-emerald-700 tracking-wider font-mono" data-testid="account-number">{accountNumber}</span>
              <button onClick={copyNumber} data-testid="copy-account-btn"
                className="p-1.5 rounded-lg hover:bg-emerald-100 text-emerald-600 transition-colors">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">Account Name</p>
              <p className="text-sm font-semibold text-gray-900">{accountName}</p>
            </div>
            <div className="flex gap-6">
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Bank</p>
                <p className="text-sm font-medium text-gray-900">{bankName}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Bank Code</p>
                <p className="text-sm font-medium text-gray-900">{bankCode}</p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SubWalletsTab({ companyId, onLoadWallet }) {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [limit, setLimit] = useState(10);

  const load = async () => {
    try {
      const res = await superAdminAPI.getSubWallets(companyId);
      setWallets(res.wallets || []);
      setLimit(res.limit || 10);
    } catch { toast.error('Failed to load sub-wallets'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [companyId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setCreating(true);
    try {
      await superAdminAPI.createSubWallet(companyId, { label: label.trim() });
      toast.success(`Sub-wallet "${label.trim()}" created`);
      setLabel('');
      setShowCreate(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create sub-wallet');
    } finally { setCreating(false); }
  };

  const handleRename = async (walletId) => {
    if (!editLabel.trim()) return;
    setRenaming(true);
    try {
      await superAdminAPI.renameSubWallet(walletId, { label: editLabel.trim() });
      toast.success('Sub-wallet renamed');
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename');
    } finally { setRenaming(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-pink-400" /></div>;

  return (
    <div className="space-y-4" data-testid="sub-wallets-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Sub-Wallets</h3>
          <p className="text-xs text-gray-400">{wallets.length} of {limit} used</p>
        </div>
        {wallets.length < limit && (
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-sub-wallet-btn"
            className="h-8 bg-pink-500 hover:bg-pink-600 text-white text-xs">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Sub-Wallet
          </Button>
        )}
      </div>

      {wallets.length === 0 ? (
        <Card className="border-gray-200">
          <CardContent className="p-8 text-center space-y-3">
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto">
              <CreditCard className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">No sub-wallets yet</p>
            <p className="text-xs text-gray-400">Create a sub-wallet to ring-fence funds for specific purposes</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {wallets.map(w => (
            <Card key={w.id} className="border-gray-200 hover:border-gray-300 transition-colors" data-testid={`sub-wallet-${w.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                      <CreditCard className="w-4 h-4 text-indigo-500" />
                    </div>
                    {editingId === w.id ? (
                      <div className="flex items-center gap-1.5">
                        <Input value={editLabel} onChange={e => setEditLabel(e.target.value)}
                          className="h-7 text-xs w-36" autoFocus data-testid="rename-input" />
                        <Button size="sm" onClick={() => handleRename(w.id)} disabled={renaming}
                          className="h-7 px-2 bg-indigo-500 hover:bg-indigo-600 text-white text-[10px]">
                          {renaming ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-7 px-2 text-[10px]">Cancel</Button>
                      </div>
                    ) : (
                      <div>
                        <span className="text-sm font-semibold text-gray-900">{w.label}</span>
                        <button onClick={() => { setEditingId(w.id); setEditLabel(w.label); }}
                          className="ml-2 text-gray-400 hover:text-gray-600" data-testid={`rename-${w.id}`}>
                          <Pencil className="w-3 h-3 inline" />
                        </button>
                      </div>
                    )}
                  </div>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${w.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                    {w.status}
                  </span>
                </div>
                <p className="text-xl font-bold text-gray-900">NGN {Number(w.balance || 0).toLocaleString()}</p>
                {w.virtual_account_number ? (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Landmark className="w-3 h-3 text-emerald-500" />
                    <span className="font-mono">{w.virtual_account_number}</span>
                    <span className="text-gray-300">|</span>
                    <span>{w.virtual_account_bank}</span>
                  </div>
                ) : (
                  <p className="text-[10px] text-orange-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> No virtual account</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-base">Create Sub-Wallet</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Wallet Name</label>
              <Input placeholder="e.g. March Stipends, Project X" value={label} data-testid="sub-wallet-label-input"
                onChange={e => setLabel(e.target.value)} required maxLength={100} />
            </div>
            <p className="text-[10px] text-gray-400">A virtual account will be automatically created on Assets MFB for this sub-wallet.</p>
            <Button type="submit" disabled={creating || !label.trim()} data-testid="confirm-create-sub-wallet"
              className="w-full bg-pink-500 hover:bg-pink-600 text-white">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Sub-Wallet'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function CompanyErrorsTab({ companyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    superAdminAPI.getTransactionErrors({ company_id: companyId, status: 'all' })
      .then(setData)
      .catch(() => toast.error('Failed to load errors'))
      .finally(() => setLoading(false));
  }, [companyId]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-pink-400" /></div>;
  const txns = data?.transactions || [];
  if (txns.length === 0) return <Card className="border-gray-200"><CardContent className="p-8 text-center text-sm text-gray-400">No transaction errors for this company</CardContent></Card>;

  return (
    <div className="space-y-2" data-testid="company-errors-tab">
      {txns.map(t => (
        <Card key={t.id} className="border-gray-200">
          <CardContent className="p-3 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-mono text-gray-400">{t.reference}</p>
              <p className="text-xs text-red-500 mt-0.5 truncate">{t.failure_reason}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-sm font-bold text-gray-900">NGN {Number(t.amount).toLocaleString()}</p>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${t.status === 'resolved' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>{t.status}</span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function CompanyDetail() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [adminForm, setAdminForm] = useState({ first_name: '', last_name: '', email: '', password: '', phone: '', role: 'admin' });
  const [submitting, setSubmitting] = useState(false);
  const [togglingPause, setTogglingPause] = useState(false);

  const load = async () => {
    try {
      const detail = await superAdminAPI.getCompanyDetail(companyId);
      setData(detail);
    } catch { toast.error('Failed to load company'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [companyId]);

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await superAdminAPI.createCompanyAdmin(companyId, adminForm);
      toast.success('Admin created');
      setShowAddAdmin(false);
      setAdminForm({ first_name: '', last_name: '', email: '', password: '', phone: '', role: 'admin' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create admin');
    } finally { setSubmitting(false); }
  };

  const handleDeleteAdmin = async (admin) => {
    if (!window.confirm(`Remove ${admin.first_name} ${admin.last_name} as admin?`)) return;
    try {
      await superAdminAPI.deleteCompanyAdmin(companyId, admin.id);
      toast.success('Admin removed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const handleToggleTransactions = async () => {
    const isLive = company.is_live !== false;
    const action = isLive ? 'pause' : 'resume';
    if (isLive) {
      if (!window.confirm(`This will immediately block all transfers, wallet operations, and payroll disbursement for ${company.name}. Are you sure?`)) return;
    }
    setTogglingPause(true);
    try {
      const result = await superAdminAPI.toggleCompanyTransactions(companyId);
      toast.success(result.message);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} transactions`);
    } finally { setTogglingPause(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;
  if (!data) return <div className="text-center py-20 text-gray-500">Company not found</div>;

  const { company, admins, employees, stats } = data;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'banking', label: 'Banking' },
    { id: 'sub-wallets', label: 'Sub-Wallets' },
    { id: 'errors', label: 'Errors' },
    { id: 'admins', label: `Admins (${stats.total_admins})` },
    { id: 'employees', label: `Employees (${stats.total_employees})` },
  ];

  return (
    <div className="space-y-6" data-testid="company-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/super-admin/companies')} className="h-8 px-2" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="w-12 h-12 rounded-xl bg-pink-50 flex items-center justify-center shrink-0">
          <span className="text-lg font-bold text-pink-600">{company.name?.[0]?.toUpperCase()}</span>
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900" data-testid="company-name">{company.name}</h1>
          <p className="text-sm text-gray-500">{company.email} {company.city ? `| ${company.city}, ${company.state}` : ''}</p>
        </div>
        <Button
          variant={company.is_live === false ? "default" : "outline"}
          size="sm"
          onClick={handleToggleTransactions}
          disabled={togglingPause}
          data-testid="toggle-transactions-btn"
          className={company.is_live === false
            ? "bg-emerald-500 hover:bg-emerald-600 text-white h-9"
            : "border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 h-9"
          }
        >
          {togglingPause ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> :
            company.is_live === false
              ? <PlayCircle className="w-4 h-4 mr-1.5" />
              : <PauseCircle className="w-4 h-4 mr-1.5" />
          }
          {company.is_live === false ? 'Resume Transactions' : 'Pause Transactions'}
        </Button>
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${company.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
          {company.status}
        </span>
      </div>

      {/* Transaction Pause Banner */}
      {company.is_live === false && (
        <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="transactions-paused-banner">
          <PauseCircle className="w-5 h-5 text-red-500 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Transactions Paused</p>
            <p className="text-xs text-red-600">All outbound transfers, payroll disbursements, and wallet sends are blocked for this company.</p>
          </div>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Employees', value: stats.total_employees, sub: `${stats.active_employees} active`, icon: Users, color: 'blue' },
          { label: 'Admins', value: stats.total_admins, sub: 'accounts', icon: Shield, color: 'purple' },
          { label: 'Monthly Payroll', value: `NGN ${Number(stats.monthly_payroll).toLocaleString()}`, sub: 'gross total', icon: DollarSign, color: 'emerald' },
          { label: 'Wallet Balance', value: `NGN ${Number(stats.wallet_balance).toLocaleString()}`, sub: 'company wallet', icon: Wallet, color: 'pink' },
        ].map(s => (
          <Card key={s.label} className="border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 rounded-lg bg-${s.color}-50 flex items-center justify-center`}>
                  <s.icon className={`w-4 h-4 text-${s.color}-500`} />
                </div>
              </div>
              <p className="text-lg font-bold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-500">{s.label} &middot; {s.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-gray-200">
            <CardContent className="p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Company Info</h3>
              {[
                ['Registration', company.slug],
                ['Email', company.email],
                ['Phone', company.phone],
                ['Address', [company.address, company.city, company.state].filter(Boolean).join(', ')],
                ['Tier', company.subscription_tier],
                ['Created', company.created_at ? new Date(company.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between">
                  <span className="text-xs text-gray-500">{label}</span>
                  <span className="text-xs font-medium text-gray-900 text-right max-w-[60%] truncate">{val || '\u2014'}</span>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card className="border-gray-200">
            <CardContent className="p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Quick Summary</h3>
              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2 text-gray-600">
                  <Shield className="w-3.5 h-3.5 text-purple-400" />
                  <span>{stats.total_admins} admin account{stats.total_admins !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Users className="w-3.5 h-3.5 text-blue-400" />
                  <span>{stats.active_employees} active employee{stats.active_employees !== 1 ? 's' : ''} of {stats.total_employees} total</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Monthly payroll: NGN {Number(stats.monthly_payroll).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{employees.filter(e => e.has_bvn).length} employee{employees.filter(e => e.has_bvn).length !== 1 ? 's' : ''} with BVN</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Landmark className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Virtual Account: {company.virtual_account_number ?
                    <span className="text-emerald-600 font-medium">{company.virtual_account_number}</span> :
                    <span className="text-orange-500">Not created</span>}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {tab === 'banking' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <BankingSection company={company} onAccountCreated={load} />
        </div>
      )}

      {tab === 'errors' && <CompanyErrorsTab companyId={companyId} />}

      {tab === 'sub-wallets' && <SubWalletsTab companyId={companyId} />}


      {tab === 'admins' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Admin Accounts</h3>
            <Button size="sm" onClick={() => setShowAddAdmin(true)} data-testid="add-admin-btn"
              className="h-8 bg-pink-500 hover:bg-pink-600 text-white text-xs">
              <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Add Admin
            </Button>
          </div>
          {admins.length === 0 ? (
            <Card className="border-gray-200"><CardContent className="p-8 text-center text-sm text-gray-400">No admin accounts yet</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {admins.map(a => (
                <Card key={a.id} className="border-gray-200" data-testid={`admin-${a.id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-purple-50 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-purple-600">{a.first_name?.[0]}{a.last_name?.[0]}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{a.first_name} {a.last_name}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{a.email}</span>
                        {a.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{a.phone}</span>}
                      </div>
                    </div>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${a.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                      {a.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-purple-50 text-purple-600">{a.role}</span>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteAdmin(a)}
                      className="h-7 w-7 p-0 text-gray-400 hover:text-red-500 hover:bg-red-50" data-testid={`delete-admin-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'employees' && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">Employees ({employees.length})</h3>
          {employees.length === 0 ? (
            <Card className="border-gray-200"><CardContent className="p-8 text-center text-sm text-gray-400">No employees yet</CardContent></Card>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2.5 px-3 font-semibold text-gray-600">Name</th>
                    <th className="text-left py-2.5 px-3 font-semibold text-gray-600">Email</th>
                    <th className="text-left py-2.5 px-3 font-semibold text-gray-600 hidden md:table-cell">Department</th>
                    <th className="text-left py-2.5 px-3 font-semibold text-gray-600 hidden md:table-cell">Job Title</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-600">Salary</th>
                    <th className="text-center py-2.5 px-3 font-semibold text-gray-600">BVN</th>
                    <th className="text-center py-2.5 px-3 font-semibold text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {employees.map(e => (
                    <tr key={e.id} className="hover:bg-gray-50/50" data-testid={`employee-${e.id}`}>
                      <td className="py-2.5 px-3 font-medium text-gray-900">{e.first_name} {e.last_name}</td>
                      <td className="py-2.5 px-3 text-gray-500">{e.email}</td>
                      <td className="py-2.5 px-3 text-gray-500 hidden md:table-cell">{e.department || '\u2014'}</td>
                      <td className="py-2.5 px-3 text-gray-500 hidden md:table-cell">{e.job_title || '\u2014'}</td>
                      <td className="py-2.5 px-3 text-right text-gray-900">{e.gross_salary ? `NGN ${Number(e.gross_salary).toLocaleString()}` : '\u2014'}</td>
                      <td className="py-2.5 px-3 text-center">
                        {e.has_bvn ? <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" /> : <XCircle className="w-4 h-4 text-gray-300 mx-auto" />}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${e.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                          {e.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Admin Dialog */}
      <Dialog open={showAddAdmin} onOpenChange={setShowAddAdmin}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-base">Add Admin for {company.name}</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateAdmin} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="First name" required value={adminForm.first_name} onChange={e => setAdminForm(p => ({...p, first_name: e.target.value}))} data-testid="admin-first-name" />
              <Input placeholder="Last name" required value={adminForm.last_name} onChange={e => setAdminForm(p => ({...p, last_name: e.target.value}))} data-testid="admin-last-name" />
            </div>
            <Input type="email" placeholder="Email" required value={adminForm.email} onChange={e => setAdminForm(p => ({...p, email: e.target.value}))} data-testid="admin-email" />
            <Input type="password" placeholder="Password (min 8 chars)" required minLength={8} value={adminForm.password} onChange={e => setAdminForm(p => ({...p, password: e.target.value}))} data-testid="admin-password" />
            <Input placeholder="Phone (optional)" value={adminForm.phone} onChange={e => setAdminForm(p => ({...p, phone: e.target.value}))} data-testid="admin-phone" />
            <Button type="submit" disabled={submitting} data-testid="submit-admin-btn"
              className="w-full bg-pink-500 hover:bg-pink-600 text-white">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Admin'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
